// LISTA DE MENSAJES AUTOMÁTICOS (filas con estado y canal). La usa la pantalla «Mensajes automáticos» con las 8 filas,
// y desde F3 (24-oct-2026) también Referidos → Mensaje (1 fila) y Membresías → Avisos (2 filas): el módulo enseña SUS
// avisos en su propia pestaña y «Editar» abre el editor de siempre (/dashboard/mensajes?evento=<clave>).
// Estados: una llamada a /messages/settings (los 7 eventos) + /google-reviews/settings si hace falta. Solo para PRO;
// a gratis se le pinta la corona.
import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Crown, Mail, Phone, ChevronRight, Power } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { isPro as checkIsPro } from '../../../utils/planPermissions';
import { messagesApi, type MessagesSettings } from '../../../api/messagesApi';
import { googleReviewsApi } from '../../../api/googleReviewsApi';
import { FILAS, type EventoClave } from '../mensajesCatalogo';

type Estado = { canal: 'whatsapp' | 'correo' | null; on: boolean };

interface Props {
  /** Claves a mostrar; sin prop = todas las que apliquen al negocio. */
  claves?: EventoClave[];
  /** Avisa cuántos están encendidos del total mostrado (para el resumen de la cabecera). */
  onCargado?: (encendidos: number, total: number) => void;
  testid?: string;
}

const FilasMensajes: React.FC<Props> = ({ claves, onCargado, testid = 'mensajes-lista' }) => {
  const { currentUser } = useAuth();
  const esPro = checkIsPro(currentUser?.subscription);
  const [estados, setEstados] = useState<Partial<Record<EventoClave, Estado>>>({});
  const [cargando, setCargando] = useState(true);

  const filas = FILAS.filter((f) => (claves ? claves.includes(f.clave) : f.requiere !== 'membresias' || currentUser?.memberships));
  const referidosOff = !currentUser?.referralsEnabled;
  const pideGoogle = filas.some((f) => f.clave === 'google_reviews');

  useEffect(() => {
    if (!esPro) { setCargando(false); return; }
    let vivo = true;
    Promise.allSettled([messagesApi.getSettings(), pideGoogle ? googleReviewsApi.summary() : Promise.reject(new Error('n/a'))]).then(([m, g]) => {
      if (!vivo) return;
      const e: Partial<Record<EventoClave, Estado>> = {};
      if (m.status === 'fulfilled') {
        const s = m.value as MessagesSettings;
        (Object.keys(s) as (keyof MessagesSettings)[]).forEach((k) => {
          const ev = s[k]; if (!ev) return;
          const correo = ev.email?.enabled === true;
          e[k] = { canal: correo ? 'correo' : (ev.enabled ? 'whatsapp' : null), on: ev.enabled === true || correo };
        });
      }
      if (g.status === 'fulfilled') e.google_reviews = { canal: g.value.config.enabled ? 'whatsapp' : null, on: !!g.value.config.enabled };
      setEstados(e); setCargando(false);
    });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esPro, pideGoogle]);

  useEffect(() => {
    if (!onCargado || cargando || !esPro) return;
    onCargado(filas.filter((f) => estados[f.clave]?.on).length, filas.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargando, estados]);

  return (
    <div className="bg-surface shadow rounded-lg border border-line divide-y divide-line animate-fade-in-up" data-testid={testid}>
      {filas.map((f) => {
        const Icon = f.icon;
        const st = estados[f.clave];
        const dependeApagado = f.requiere === 'referidos' && referidosOff;
        return (
          <RouterLink
            key={f.clave}
            to={`/dashboard/mensajes?evento=${f.clave}`}
            data-testid={`fila-${f.clave}`}
            className="flex items-center gap-3 px-4 py-3.5 hover:bg-surface-2 transition-colors"
          >
            <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-surface-2 ${f.color}`}><Icon className="h-5 w-5" /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-content">
                {f.nombre}
                {f.alDueno && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-content-3 whitespace-nowrap">a ti</span>}
              </span>
              <span className="block text-xs text-content-3 sm:truncate">{f.cuando}</span>
              {dependeApagado && (
                <span className="block text-xs text-amber-600 dark:text-amber-400 mt-0.5">
                  Se activa al encender referidos en Referidos → Ajustes.
                </span>
              )}
            </span>
            {esPro && !cargando && (
              <span className="flex-shrink-0 flex items-center gap-2 text-xs" data-testid="fila-estado">
                {st?.on ? (
                  <>
                    <span className="inline-flex items-center gap-1 text-content-2">
                      {st.canal === 'correo' ? <Mail className="h-3.5 w-3.5" /> : <Phone className="h-3.5 w-3.5" />}
                      <span className="hidden sm:inline">{st.canal === 'correo' ? 'Correo' : 'WhatsApp'}</span>
                    </span>
                    <span className="rounded px-1.5 py-0.5 font-bold uppercase tracking-wide bg-green-500/15 text-green-700 dark:text-green-300">Encendido</span>
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium text-content-3 bg-surface-2"><Power className="h-3 w-3" /> Apagado</span>
                )}
              </span>
            )}
            {!esPro && <Crown className="h-4 w-4 text-yellow-500 flex-shrink-0" />}
            <ChevronRight className="h-4 w-4 text-content-3 flex-shrink-0" />
          </RouterLink>
        );
      })}
    </div>
  );
};

export default FilasMensajes;
